import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { EditIcon, WarningIcon } from '../../../components/icons';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import { HelpTrigger } from '../../../help';
import AdminPageShell from '../../components/AdminPageShell';
import AdminTabs from '../../components/AdminTabs';
import AdminModal from '../../components/AdminModal';
import FieldList from '../../components/FieldList';
import EntityAuditList from '../../components/EntityAuditList';
import { formatDate } from '../../lib/format';
import CycleMilestonesCard, { type CycleMilestone } from '../elections/CycleMilestonesCard';
import { CYCLES_QUERY_KEY } from './CyclesView';
import CycleMembersTab from './CycleMembersTab';
import { CycleChips, type CycleSession, type LegislativeBody, type LegislativeCycleRow } from './cycleTypes';

interface CycleDetail extends Omit<LegislativeCycleRow, 'milestone_count' | 'sessions'> {
  body: LegislativeBody;
  sessions: CycleSession[];
  milestones: CycleMilestone[];
}

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'members', label: 'Members' },
  { key: 'activity', label: 'Activity' },
];

const labelClasses = 'mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary';

const apiError = (error: any, fallback: string) =>
  error?.response?.data?.error ?? error?.response?.data?.message ?? fallback;

const EMPTY_EDIT = { label: '', number: '', start_date: '', end_date: '', dates_confirmed: false, notes: '' };

/**
 * One legislative cycle (Documentation/ELECTION_OUTCOMES_PRD.md §5.0): its dates,
 * the sessions inside it, its milestones (the shared Milestones card, legislative
 * domain), and its audit trail. Cycles of a body are back to back, so moving a
 * date here also moves the neighboring cycle's matching date.
 */
const CycleDetailPage: React.FC = () => {
  const { cycleId } = useParams<{ cycleId: string }>();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('overview');
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_EDIT);

  const cycleQueryKey = ['admin-legislature', 'cycle', cycleId] as const;
  const detail = useQuery({
    queryKey: cycleQueryKey,
    queryFn: async () => {
      const response = await api.get(`/admin/legislature/cycles/${cycleId}`);
      return response.data.data as CycleDetail;
    },
    enabled: !!cycleId,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-legislature', 'cycle'] });
    queryClient.invalidateQueries({ queryKey: CYCLES_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ['admin-accounts', 'audit', 'legislative_cycle', cycleId ?? ''] });
  };

  const saveMutation = useMutation({
    mutationFn: () =>
      api.put(`/admin/legislature/cycles/${cycleId}`, {
        label: editForm.label.trim(),
        number: editForm.number.trim() === '' ? null : parseInt(editForm.number, 10),
        start_date: editForm.start_date,
        end_date: editForm.end_date,
        dates_confirmed: editForm.dates_confirmed,
        notes: editForm.notes.trim(),
      }),
    onSuccess: () => {
      toast.success('Cycle updated');
      setEditOpen(false);
      invalidate();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to update cycle')),
  });

  if (detail.isLoading) {
    return (
      <AdminPageShell title="Legislative Cycle" backUrl="/admin-next/legislature?tab=cycles">
        <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">Loading cycle…</p>
      </AdminPageShell>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <AdminPageShell title="Legislative Cycle" backUrl="/admin-next/legislature?tab=cycles">
        <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
          <WarningIcon className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
          <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">Couldn't load this cycle.</p>
          <Button variant="outline" size="sm" onClick={() => detail.refetch()}>Retry</Button>
        </div>
      </AdminPageShell>
    );
  }

  const cycle = detail.data;
  const editDisabled = !editForm.label.trim() || !editForm.start_date || !editForm.end_date || editForm.end_date <= editForm.start_date;

  return (
    <AdminPageShell
      title={cycle.label}
      description={cycle.body.name}
      backUrl="/admin-next/legislature?tab=cycles"
      actions={
        <Button variant="outline" size="sm" icon={EditIcon}
          onClick={() => {
            setEditForm({
              label: cycle.label,
              number: cycle.number === null ? '' : String(cycle.number),
              start_date: cycle.start_date,
              end_date: cycle.end_date,
              dates_confirmed: cycle.dates_confirmed,
              notes: cycle.notes ?? '',
            });
            setEditOpen(true);
          }}>
          Edit Cycle
        </Button>
      }
    >
      <AdminTabs tabs={TABS} activeKey={tab} onChange={setTab} />

      {tab === 'overview' && (
        <div className="space-y-4">
          <div className="card p-5">
            <FieldList
              fields={[
                {
                  label: 'Cycle',
                  value: (
                    <span className="inline-flex flex-wrap items-center">
                      {cycle.label}
                      <CycleChips cycle={cycle} />
                    </span>
                  ),
                },
                { label: 'Legislative body', value: cycle.body.name },
                { label: 'Dates', value: `${formatDate(cycle.start_date)} – ${formatDate(cycle.end_date)}` },
                {
                  label: 'Date status',
                  value: cycle.dates_confirmed
                    ? 'Confirmed'
                    : 'Expected — computed from the constitutional rule until the convene date is confirmed',
                },
                { label: 'Notes', value: cycle.notes ?? '—', fullWidth: true },
              ]}
            />
          </div>

          {cycle.body.has_sessions && (
            <div className="card p-5">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <h4 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">Sessions</h4>
                <Link to="/admin-next/legislature?tab=sessions"
                  className="text-sm font-medium text-primary-600 hover:underline dark:text-primary-400">
                  Edit in Sessions
                </Link>
              </div>
              <p className="mb-3 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                A regular session's convene date is this cycle's start, so it is entered once, on the session.
              </p>
              {cycle.sessions.length === 0 ? (
                <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">No sessions registered in this cycle yet.</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {cycle.sessions.map((session) => (
                    <li key={session.session_key} className="flex flex-wrap gap-x-3">
                      <span className="font-medium text-text-primary dark:text-text-dark-primary">{session.label}</span>
                      <span className="text-text-secondary dark:text-text-dark-secondary">
                        {session.convene_date
                          ? `${formatDate(session.convene_date)} – ${session.adjourn_date ? formatDate(session.adjourn_date) : 'in session'}`
                          : 'Dates needed'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <CycleMilestonesCard
            cycleId={cycle.id}
            domain="legislative"
            milestones={cycle.milestones}
            sessions={cycle.body.has_sessions ? cycle.sessions : undefined}
            cycleQueryKey={cycleQueryKey}
            onChanged={invalidate}
          />
        </div>
      )}

      {tab === 'members' && cycleId && (
        <CycleMembersTab cycleId={cycleId} started={cycle.start_date <= new Date().toISOString().slice(0, 10)} />
      )}

      {tab === 'activity' && cycleId && (
        <EntityAuditList entityType="legislative_cycle" entityId={cycleId} />
      )}

      <AdminModal
        isOpen={editOpen}
        title="Edit Cycle"
        onClose={() => setEditOpen(false)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button variant="primary" size="sm" disabled={editDisabled}
              loading={saveMutation.isLoading} onClick={() => saveMutation.mutate()}>
              Save Cycle
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="edit-cycle-label" className={labelClasses}>Label *</label>
              <input id="edit-cycle-label" type="text" value={editForm.label} maxLength={100} className="input"
                onChange={(event) => setEditForm((current) => ({ ...current, label: event.target.value }))} />
            </div>
            <div>
              <label htmlFor="edit-cycle-number" className={labelClasses}>Number</label>
              <input id="edit-cycle-number" type="number" min={1} value={editForm.number} className="input"
                onChange={(event) => setEditForm((current) => ({ ...current, number: event.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="edit-cycle-start" className={labelClasses}>Start date *</label>
              <input id="edit-cycle-start" type="date" value={editForm.start_date} className="input"
                onChange={(event) => setEditForm((current) => ({ ...current, start_date: event.target.value }))} />
            </div>
            <div>
              <label htmlFor="edit-cycle-end" className={labelClasses}>End date *</label>
              <input id="edit-cycle-end" type="date" value={editForm.end_date} className="input"
                onChange={(event) => setEditForm((current) => ({ ...current, end_date: event.target.value }))} />
            </div>
          </div>
          <p className="flex items-start gap-1.5 text-xs text-text-tertiary dark:text-text-dark-tertiary">
            <span>
              Cycles run back to back: moving the start also ends the previous cycle the day before, and moving
              the end starts the next cycle the day after.
            </span>
            <HelpTrigger contentKey="legislative-cycles" size="sm" ariaLabel="What changing a cycle's dates does" />
          </p>
          <div className="flex items-center gap-2">
            <input id="edit-cycle-confirmed" type="checkbox" checked={editForm.dates_confirmed}
              onChange={(event) => setEditForm((current) => ({ ...current, dates_confirmed: event.target.checked }))}
              className="h-4 w-4 rounded border-border-light-strong text-primary-600 focus:ring-primary-500 dark:border-border-dark" />
            <label htmlFor="edit-cycle-confirmed" className="text-sm text-text-primary dark:text-text-dark-primary">
              Dates are confirmed
            </label>
          </div>
          <div>
            <label htmlFor="edit-cycle-notes" className={labelClasses}>Notes</label>
            <textarea id="edit-cycle-notes" rows={2} value={editForm.notes} className="input"
              onChange={(event) => setEditForm((current) => ({ ...current, notes: event.target.value }))} />
          </div>
        </div>
      </AdminModal>
    </AdminPageShell>
  );
};

export default CycleDetailPage;
